// labelChips.ts — CTSK-507. The group / collection / folder chips on a search
// result row and the entries of the related-findings strip share one set of
// rules, so the dropdown, the full page and the strip cannot drift:
//   • a chip SENDS the stored value (original casing; the full folder path),
//     never the lower-cased or shortened text it displays;
//   • group and collection compare case-insensitively on the server, folder
//     case-sensitively — which is why the value must be the stored one;
//   • a folder shows its LAST segment ("Taxes 2025/Receipts/Q3" -> "Q3") and
//     carries the full path in its tooltip.

import type { DocumentSearchFacets, DocumentSearchResult } from '@captain-tasko/shared';

export type LabelKind = 'group' | 'collection' | 'folder';

export interface LabelChip {
  kind: LabelKind;
  /** The stored value — what the filter takes. */
  value: string;
  /** What the chip shows. */
  text: string;
  /** Tooltip: names the kind and, for a folder, the full path. */
  title: string;
}

const KIND_LABEL: Record<LabelKind, string> = { group: 'Group', collection: 'Collection', folder: 'Folder' };

export function lastFolderSegment(path: string): string {
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? path;
}

export function chipFor(kind: LabelKind, value: string): LabelChip {
  return {
    kind,
    value,
    text: kind === 'folder' ? lastFolderSegment(value) : value,
    title: `${KIND_LABEL[kind]}: ${value}`,
  };
}

/** Up to three chips for a result row, only for the labels that are present. */
export function labelChipsFor(result: Pick<DocumentSearchResult, 'group' | 'collection' | 'folder'>): LabelChip[] {
  const out: LabelChip[] = [];
  if (result.group) out.push(chipFor('group', result.group));
  if (result.collection) out.push(chipFor('collection', result.collection));
  if (result.folder) out.push(chipFor('folder', result.folder));
  return out;
}

export interface FacetEntry extends LabelChip {
  count: number;
}

/** Facet entries across label types, biggest first (ties keep group, collection, folder order). */
export function facetEntries(facets: DocumentSearchFacets | undefined, max = 8): FacetEntry[] {
  if (!facets) return [];
  const all: FacetEntry[] = [];
  for (const kind of ['group', 'collection', 'folder'] as const) {
    for (const f of facets[kind] ?? []) all.push({ ...chipFor(kind, f.value), count: f.count });
  }
  return all
    .map((e, i) => ({ e, i }))
    .sort((a, b) => b.e.count - a.e.count || a.i - b.i)
    .slice(0, max)
    .map((x) => x.e);
}
